import { isAxiosError } from "axios";
import { type FC } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";

import { serverCreateLinkedSections } from "api/api-server-publishing";
import { CourseMenu } from "components/menus/CourseMenu";
import { SectionMenu } from "components/menus/SectionMenu";
import { TermMenu } from "components/menus/TermMenu";
import { SectionKind } from "components/server-types";
import { t } from "i18n/i18n";
import { FormField } from "mds/components/FormField";
import { RadioButtonCard } from "mds/components/RadioButtonCard";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { BookSectionIcon, BookSectionLinkedIcon, BookSectionTemplateIcon } from "mds/icons";
import { storeApi } from "store/index";
import { toastLocalizedOperationCatcher, toastSuccessMessage } from "utils/alerts";
import { isCCMEnabled } from "utils/waffle-flags";

const SECTION_KIND_OPTIONS = [
  {
    kind: SectionKind.UNLINKED,
    icon: <BookSectionIcon size="m" />,
    labelKey: "home.add_section_modal.section_type_default_label",
    descriptionKey: "home.add_section_modal.section_type_default_description",
  },
  {
    kind: SectionKind.TEMPLATE,
    icon: <BookSectionTemplateIcon size="m" />,
    labelKey: "home.add_section_modal.section_type_template_label",
    descriptionKey: "home.add_section_modal.section_type_template_description",
  },
  {
    kind: SectionKind.LINKED,
    icon: <BookSectionLinkedIcon size="m" />,
    labelKey: "home.add_section_modal.section_type_linked_label",
    descriptionKey: "home.add_section_modal.section_type_linked_description",
  },
] as const;

type AddSectionModalProps = {
  eventName?: string;
  eventProperties?: Record<string, unknown>;
  onClose: () => void;
  defaultCourseId?: string | null;
  defaultTermId?: string | null;
  defaultTemplateId?: string | null;
};

type AddSectionFormValues = {
  title: string;
  termId: string;
  courseId: string;
  sectionKind: SectionKind | undefined;
  templateId: string | null;
  linkedSectionCount: number;
};

export const AddSectionModal: FC<AddSectionModalProps> = ({
  eventName,
  eventProperties,
  onClose,
  defaultCourseId,
  defaultTermId,
  defaultTemplateId,
}) => {
  const ccmEnabled = isCCMEnabled();
  const defaultSectionKind = ccmEnabled
    ? defaultTemplateId
      ? SectionKind.LINKED
      : undefined
    : SectionKind.UNLINKED;

  const form = useForm<AddSectionFormValues>({
    defaultValues: {
      title: "",
      termId: defaultTermId || "",
      courseId: defaultCourseId || "",
      sectionKind: defaultSectionKind,
      templateId: defaultTemplateId || null,
      linkedSectionCount: 1,
    },
  });

  const termId = form.watch("termId");
  const courseId = form.watch("courseId");
  const sectionKind = form.watch("sectionKind");
  const templateId = form.watch("templateId");

  // Custom controls (menus / radios) need explicit registration for submit-time validation.
  form.register("termId", {
    validate: (value) => !!value || t("error.field_required"),
  });
  form.register("courseId", {
    validate: (value) => !!value || t("error.field_required"),
  });
  form.register("sectionKind", {
    validate: (value) => !ccmEnabled || value != null || t("error.field_required"),
  });
  form.register("templateId", {
    validate: (value, values) =>
      values.sectionKind !== SectionKind.LINKED || !!value || t("error.field_required"),
  });

  const onSubmit: SubmitHandler<AddSectionFormValues> = async ({
    title,
    termId: submittedTermId,
    courseId: submittedCourseId,
    sectionKind: submittedSectionKind,
    templateId: submittedTemplateId,
    linkedSectionCount,
  }) => {
    const trimmedTitle = title.trim();

    if (submittedSectionKind === SectionKind.LINKED) {
      try {
        await serverCreateLinkedSections(submittedTemplateId!, linkedSectionCount, trimmedTitle);
        toastSuccessMessage(t("home.toasts.section_added"));
        onClose();
      } catch (error) {
        toastLocalizedOperationCatcher("create_failure", {
          item: t("glossary.sections"),
        })(error);
      }
      return;
    }

    form.clearErrors("title");
    try {
      await storeApi.sections.create(
        {
          course_id: submittedCourseId,
          kind: submittedSectionKind,
          term_id: submittedTermId || undefined,
          title: trimmedTitle,
        },
        { skipToast: true },
      );
      toastSuccessMessage(t("home.toasts.section_added"));
      onClose();
    } catch (error: unknown) {
      if (!isAxiosError(error) || error.response?.status !== 400) {
        toastLocalizedOperationCatcher("create_failure", {
          item: t("glossary.sections"),
        })(error);
        return;
      }

      const data = error.response?.data as { title?: string[] };
      const titleErrMsg = Array.isArray(data?.title) ? data.title[0] : data?.title;

      if (typeof titleErrMsg === "string" && titleErrMsg === "err_section_title_unavailable") {
        form.setError("title", {
          type: "server",
          message: t("home.add_section_modal.title_unavailable"),
        });
      } else {
        toastLocalizedOperationCatcher("create_failure", {
          item: t("glossary.sections"),
        })(error as Error);
      }
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-3">
          <div className="flex gap-4">
            <FormField
              className="min-w-0 flex-1"
              label={t("home.terms.select_term_label")}
              name="termId"
              required
            >
              {(controlProps) => (
                <TermMenu
                  {...controlProps}
                  className="w-full"
                  defaultToAll={false}
                  selectedTermId={termId}
                  onTermChange={(id) =>
                    form.setValue("termId", id, { shouldDirty: true, shouldValidate: true })
                  }
                />
              )}
            </FormField>

            <FormField
              className="min-w-0 flex-1"
              label={t("home.courses.select_course_label")}
              name="courseId"
              required
            >
              {(controlProps) => (
                <CourseMenu
                  {...controlProps}
                  className="w-full"
                  defaultToAll={false}
                  selectedCourseId={courseId}
                  adminAccessibleOnly
                  onCourseChange={(id) =>
                    form.setValue("courseId", id, { shouldDirty: true, shouldValidate: true })
                  }
                />
              )}
            </FormField>
          </div>

          <FormField label={t("home.add_section_modal.section_title_label")} name="title" required>
            {(controlProps) => (
              <TextField
                {...form.register("title", {
                  required: t("error.field_required"),
                  validate: (value) => value.trim().length > 0 || t("error.field_required"),
                  onChange: () => form.clearErrors("title"),
                })}
                {...controlProps}
                eventName={eventName}
                eventProperties={eventProperties}
                placeholder={t("home.placeholder.section_title")}
              />
            )}
          </FormField>

          {ccmEnabled && (
            <FormField
              label={t("home.add_section_modal.section_type_label")}
              name="sectionKind"
              required
            >
              {() => (
                <div className="flex flex-col gap-2">
                  {SECTION_KIND_OPTIONS.map(({ kind, icon, labelKey, descriptionKey }) => (
                    <RadioButtonCard
                      checked={sectionKind === kind}
                      description={t(descriptionKey)}
                      icon={icon}
                      id={`section-kind-${kind}`}
                      key={kind}
                      label={t(labelKey)}
                      name="section-kind"
                      value={kind}
                      onChange={() =>
                        form.setValue("sectionKind", kind, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                    >
                      {kind === SectionKind.LINKED && sectionKind === SectionKind.LINKED && (
                        <div className="flex gap-4">
                          <FormField
                            className="min-w-0 flex-1"
                            label={t("home.add_section_modal.template_label")}
                            name="templateId"
                            required
                          >
                            {(controlProps) => (
                              <SectionMenu
                                {...controlProps}
                                courseId={courseId}
                                emptyOptionText={t("home.add_section_modal.select_template")}
                                selectedSectionId={templateId}
                                templatesOnly
                                onSectionChange={(id) =>
                                  form.setValue("templateId", id || null, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  })
                                }
                              />
                            )}
                          </FormField>

                          <FormField
                            className="min-w-0 flex-1"
                            label={t("home.add_section_modal.sections_label")}
                            name="linkedSectionCount"
                            required
                          >
                            {(controlProps) => (
                              <TextField
                                {...form.register("linkedSectionCount", {
                                  valueAsNumber: true,
                                  required: t("error.field_required"),
                                  validate: (value) =>
                                    (Number.isInteger(value) && value >= 1 && value <= 10) ||
                                    t("error.field_required"),
                                })}
                                {...controlProps}
                                // Custom styling to match Listbox xs height (TextField has no xs size).
                                className="body-xs h-[30px] rounded-[6px] px-2 py-0 supports-squircle:rounded-[12px]"
                                max={10}
                                min={1}
                                type="number"
                              />
                            )}
                          </FormField>
                        </div>
                      )}
                    </RadioButtonCard>
                  ))}
                </div>
              )}
            </FormField>
          )}
        </div>

        <div className="mt-8 flex w-full justify-end gap-2">
          <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
            {t("common.cancel")}
          </SquareButton>

          <SquareButton
            kind="accent"
            size="m"
            title={t("home.add_section_modal.create_section")}
            type="submit"
          >
            {t("home.add_section_modal.create_section")}
          </SquareButton>
        </div>
      </form>
    </FormProvider>
  );
};
